// `MediaTrackCapabilities` and `MediaTrackConstraintSet` in lib.dom.d.ts don't
// yet include the Image Capture extensions (`focusMode`, `pointsOfInterest`,
// `exposureMode`, `whiteBalanceMode`). These narrowing helpers replace the
// previous `as unknown as` casts: they let us probe the runtime shape and use
// the inferred types from there.

type FocusModeCapabilities = MediaTrackCapabilities & { focusMode: string[] };
type PointsOfInterestCapabilities = MediaTrackCapabilities & {
  pointsOfInterest: unknown;
};
type ExposureModeCapabilities = MediaTrackCapabilities & {
  exposureMode: string[];
};
type WhiteBalanceModeCapabilities = MediaTrackCapabilities & {
  whiteBalanceMode: string[];
};

type ImageCaptureConstraintSet = MediaTrackConstraintSet & {
  focusMode?: string;
  pointsOfInterest?: { x: number; y: number }[];
  exposureMode?: string;
  whiteBalanceMode?: string;
};

function supportsContinuousMode<K extends string>(
  caps: MediaTrackCapabilities,
  key: K
): caps is MediaTrackCapabilities & Record<K, string[]> {
  const value = (caps as Record<K, unknown>)[key];
  return Array.isArray(value) && value.includes("continuous");
}

function supportsFocusMode(
  caps: MediaTrackCapabilities
): caps is FocusModeCapabilities {
  return supportsContinuousMode(caps, "focusMode");
}

function supportsPointsOfInterest(
  caps: MediaTrackCapabilities
): caps is PointsOfInterestCapabilities {
  return (
    (caps as { pointsOfInterest?: unknown }).pointsOfInterest !== undefined
  );
}

function supportsExposureMode(
  caps: MediaTrackCapabilities
): caps is ExposureModeCapabilities {
  return supportsContinuousMode(caps, "exposureMode");
}

function supportsWhiteBalanceMode(
  caps: MediaTrackCapabilities
): caps is WhiteBalanceModeCapabilities {
  return supportsContinuousMode(caps, "whiteBalanceMode");
}

export async function applyCameraConstraints(
  track: MediaStreamTrack
): Promise<void> {
  if (typeof track.getCapabilities !== "function") {
    return;
  }

  let caps: MediaTrackCapabilities;
  try {
    caps = track.getCapabilities();
  } catch (err) {
    console.warn("[BarcodeScanner] getCapabilities threw:", err);
    return;
  }

  const advanced: ImageCaptureConstraintSet[] = [];

  if (supportsFocusMode(caps)) {
    advanced.push({ focusMode: "continuous" });
  }

  if (supportsPointsOfInterest(caps)) {
    advanced.push({ pointsOfInterest: [{ x: 0.5, y: 0.5 }] });
  }

  if (supportsExposureMode(caps)) {
    advanced.push({ exposureMode: "continuous" });
  }

  if (supportsWhiteBalanceMode(caps)) {
    advanced.push({ whiteBalanceMode: "continuous" });
  }

  if (advanced.length === 0) {
    return;
  }

  try {
    await track.applyConstraints({ advanced });
  } catch (err) {
    console.warn("[BarcodeScanner] applyConstraints failed:", err);
  }
}
